import type { CharacterGridSpec } from "@/character/grid";
import { parseSkillSpritesheet } from "@/character/skills/parse-skill-sheet";
import {
  sheetLayoutFromGrid,
  startRowLoop,
} from "@/character/sheet-preview-canvas";
import {
  defaultSkillFrameLayout,
  normalizeSkillFrameLayout,
  type SkillFrameLayout,
  type SkillFrameRect,
} from "@/character/skills/skill-frame-layout";

const DISPLAY_MAX_W = 640;

type DragState =
  | { kind: "move"; index: number; startX: number; startY: number; orig: SkillFrameRect }
  | { kind: "resize"; index: number; startX: number; startY: number; orig: SkillFrameRect };

export function mountSkillSheetPreview(
  host: HTMLElement,
  dataUrl: string,
  layout: SkillFrameLayout,
  onLayoutChange: (layout: SkillFrameLayout) => void,
): () => void {
  let currentLayout = layout;
  let displayScale = 1;
  let drag: DragState | null = null;
  let previewStripUrl: string | null = null;
  let stopLoop: (() => void) | null = null;
  let previewGrid: CharacterGridSpec | null = null;

  host.innerHTML = `
    <section class="character-grid-panel sheet-upload-preview" aria-labelledby="skill-preview-title">
      <div class="grid-panel-head">
        <h2 id="skill-preview-title" class="grid-panel-title">ครอบ 6 เฟรม</h2>
        <p class="hint grid-panel-meta">ลากกรอบ 1–6 วางบนภาพ · ลากมุมล่างขวาปรับขนาด · ล่างสุด = ตัวอย่างเล่น loop</p>
      </div>
      <div class="grid-align-tools">
        <button type="button" class="btn btn-ghost" id="skill-frames-reset">รีเซ็ต 6 กรอบ</button>
      </div>
      <div class="skill-frame-editor-wrap">
        <canvas class="skill-frame-editor-canvas" aria-label="ลากกรอบเฟรม"></canvas>
      </div>
      <div class="grid-loop-list">
        <article class="grid-loop-row">
          <span class="grid-loop-label">SKILL</span>
          <div class="grid-loop-body">
            <canvas class="grid-loop-player skill-frame-strip-preview" role="img" aria-label="ตัวอย่างสกิล"></canvas>
          </div>
        </article>
      </div>
    </section>
  `;

  const metaEl = host.querySelector<HTMLElement>(".grid-panel-meta");
  const wrapEl = host.querySelector<HTMLElement>(".skill-frame-editor-wrap");
  const canvas = host.querySelector<HTMLCanvasElement>(".skill-frame-editor-canvas");
  const stripCanvas = host.querySelector<HTMLCanvasElement>(".skill-frame-strip-preview");

  const img = new Image();
  img.decoding = "async";

  const setLayout = (next: SkillFrameLayout) => {
    if (!img.naturalWidth) {
      currentLayout = next;
      return;
    }
    currentLayout = normalizeSkillFrameLayout(next, img.naturalWidth, img.naturalHeight);
    onLayoutChange(currentLayout);
    void refreshPreviewStrip();
    paint();
  };

  const refreshPreviewStrip = async () => {
    try {
      const parsed = await parseSkillSpritesheet(dataUrl, currentLayout);
      previewStripUrl = parsed.spritesheetDataUrl;
      previewGrid = parsed.grid;
      drawStripPreview();
    } catch {
      previewStripUrl = null;
    }
  };

  const drawStripPreview = () => {
    stopLoop?.();
    stopLoop = null;
    if (!stripCanvas || !previewStripUrl) return;
    const stripImg = new Image();
    stripImg.onload = () => {
      if (!previewGrid) return;
      const parsedLayout = sheetLayoutFromGrid(stripImg, previewGrid);
      stripCanvas.width = 112;
      stripCanvas.height = 112;
      stopLoop = startRowLoop(stripCanvas, stripImg, parsedLayout, 0, 14, "loop");
    };
    stripImg.src = previewStripUrl;
  };

  const computeDisplayScale = (): number => {
    const iw = img.naturalWidth;
    if (!iw) return 1;
    const pad = 12;
    const wrapW = wrapEl?.clientWidth ? wrapEl.clientWidth - pad : DISPLAY_MAX_W;
    const cap = Math.min(DISPLAY_MAX_W, Math.max(160, wrapW));
    return Math.min(1, cap / iw);
  };

  const paint = () => {
    if (!canvas || !img.naturalWidth) return;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    displayScale = computeDisplayScale();
    const dw = Math.round(iw * displayScale);
    const dh = Math.round(ih * displayScale);
    canvas.width = dw;
    canvas.height = dh;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, dw, dh);
    ctx.drawImage(img, 0, 0, dw, dh);

    currentLayout.frames.forEach((fr, index) => {
      const x = fr.x * displayScale;
      const y = fr.y * displayScale;
      const w = fr.w * displayScale;
      const h = fr.h * displayScale;
      ctx.strokeStyle = "rgba(110, 200, 255, 0.95)";
      ctx.lineWidth = 2;
      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      ctx.fillStyle = "rgba(110, 200, 255, 0.12)";
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = "#e8f4ff";
      ctx.font = "bold 14px system-ui, sans-serif";
      ctx.fillText(String(index + 1), x + 6, y + 18);
      const handle = 10;
      ctx.fillStyle = "rgba(110, 200, 255, 0.9)";
      ctx.fillRect(x + w - handle, y + h - handle, handle, handle);
    });

    if (metaEl) {
      metaEl.textContent = `${iw}×${ih} px · ลากกรอบแต่ละเฟรม`;
    }
  };

  const pointerToImage = (clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!canvas || !img.naturalWidth) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = ((clientX - rect.left) / rect.width) * img.naturalWidth;
    const y = ((clientY - rect.top) / rect.height) * img.naturalHeight;
    return { x, y };
  };

  const hitTest = (ix: number, iy: number): { index: number; resize: boolean } | null => {
    if (!canvas || !img.naturalWidth) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = img.naturalWidth / Math.max(1, rect.width);
    const handle = 12 * scaleX;
    for (let i = currentLayout.frames.length - 1; i >= 0; i -= 1) {
      const fr = currentLayout.frames[i];
      if (
        ix >= fr.x + fr.w - handle &&
        ix <= fr.x + fr.w &&
        iy >= fr.y + fr.h - handle &&
        iy <= fr.y + fr.h
      ) {
        return { index: i, resize: true };
      }
      if (ix >= fr.x && ix <= fr.x + fr.w && iy >= fr.y && iy <= fr.y + fr.h) {
        return { index: i, resize: false };
      }
    }
    return null;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!canvas) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt) return;
    const hit = hitTest(pt.x, pt.y);
    if (!hit) return;
    canvas.setPointerCapture(event.pointerId);
    const orig = { ...currentLayout.frames[hit.index] };
    drag = hit.resize
      ? { kind: "resize", index: hit.index, startX: pt.x, startY: pt.y, orig }
      : { kind: "move", index: hit.index, startX: pt.x, startY: pt.y, orig };
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag || !img.naturalWidth) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt) return;
    const maxW = img.naturalWidth;
    const maxH = img.naturalHeight;
    const frames = currentLayout.frames.map((f) => ({ ...f }));
    const fr = frames[drag.index];
    const dx = pt.x - drag.startX;
    const dy = pt.y - drag.startY;

    if (drag.kind === "move") {
      fr.x = Math.max(0, Math.min(drag.orig.x + dx, maxW - fr.w));
      fr.y = Math.max(0, Math.min(drag.orig.y + dy, maxH - fr.h));
    } else {
      fr.w = Math.max(8, Math.min(drag.orig.w + dx, maxW - fr.x));
      fr.h = Math.max(8, Math.min(drag.orig.h + dy, maxH - fr.y));
    }
    currentLayout = { frames };
    paint();
  };

  const onPointerUp = (event: PointerEvent) => {
    if (!drag) return;
    canvas?.releasePointerCapture(event.pointerId);
    drag = null;
    setLayout(currentLayout);
  };

  canvas?.addEventListener("pointerdown", onPointerDown);
  canvas?.addEventListener("pointermove", onPointerMove);
  canvas?.addEventListener("pointerup", onPointerUp);
  canvas?.addEventListener("pointercancel", onPointerUp);

  host.querySelector("#skill-frames-reset")?.addEventListener("click", () => {
    if (!img.naturalWidth) return;
    setLayout(defaultSkillFrameLayout(img.naturalWidth, img.naturalHeight));
  });

  img.onload = () => {
    if (!currentLayout.frames.length) {
      currentLayout = defaultSkillFrameLayout(img.naturalWidth, img.naturalHeight);
    } else {
      currentLayout = normalizeSkillFrameLayout(currentLayout, img.naturalWidth, img.naturalHeight);
    }
    void refreshPreviewStrip();
    paint();
    requestAnimationFrame(() => paint());
  };
  img.src = dataUrl;

  const resizeObserver =
    typeof ResizeObserver !== "undefined" && wrapEl
      ? new ResizeObserver(() => {
          if (!img.naturalWidth) return;
          paint();
        })
      : null;
  resizeObserver?.observe(wrapEl!);

  return () => {
    resizeObserver?.disconnect();
    stopLoop?.();
    canvas?.removeEventListener("pointerdown", onPointerDown);
    canvas?.removeEventListener("pointermove", onPointerMove);
    canvas?.removeEventListener("pointerup", onPointerUp);
    canvas?.removeEventListener("pointercancel", onPointerUp);
    host.replaceChildren();
  };
}
